/**
 * Login Page
 */

import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { login } from '../redux/slices/authSlice';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const APP_CODE = process.env.REACT_APP_ALICEBLUE_APP_CODE || 'rYF3MOqmmn';

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state) => state.auth);
  
  const [userId, setUserId] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [localError, setLocalError] = useState('');
  const [validatingAccess, setValidatingAccess] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLocalError(''); // Clear previous errors
    
    // First validate user access
    setValidatingAccess(true);
    try {
      const accessResponse = await axios.post(`${API_URL}/api/user-access/validate-access`, {
        userId: userId
      });

      if (!accessResponse.data.success) {
        setLocalError(accessResponse.data.message || 'Access denied');
        setValidatingAccess(false);
        return;
      }

      // If access is valid, proceed with login
      console.log('Access validated, attempting login with:', { userId, authCode, appCode: APP_CODE });
      const result = await dispatch(login({ userId, authCode, appCode: APP_CODE }));
      console.log('Login result:', result);
      
      if (result.type === 'auth/login/fulfilled') {
        console.log('Login successful, navigating to dashboard');
        navigate('/dashboard');
      } else {
        const errorMsg = result.payload?.error || result.error?.message || 'Login failed';
        setLocalError(errorMsg);
        console.error('Login failed:', errorMsg);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Access validation failed. Please contact administrator.';
      setLocalError(errorMsg);
      console.error('Access validation error:', err);
    } finally {
      setValidatingAccess(false);
    }
  };

  // Handle Get Auth Code button click
  const handleGetAuthCode = async () => {
    if (!userId) {
      setLocalError('Please enter your User ID first');
      return;
    }

    // Validate user access before redirecting to AliceBlue
    setValidatingAccess(true);
    setLocalError('');

    try {
      const accessResponse = await axios.post(`${API_URL}/api/user-access/validate-access`, {
        userId: userId
      });

      if (!accessResponse.data.success) {
        setLocalError(accessResponse.data.message || 'Access denied. Please contact administrator.');
        setValidatingAccess(false);
        return;
      }

      // If access is valid, proceed with AliceBlue OAuth
      const redirectUrl = encodeURIComponent(`${window.location.origin}/auth-callback`);
      const aliceBlueAuthUrl = `https://ant.aliceblueonline.com/?appcode=${APP_CODE}&redirect_url=${redirectUrl}`;
      
      // Store userId in sessionStorage so we can retrieve it after redirect
      sessionStorage.setItem('pendingUserId', userId);
      
      window.location.href = aliceBlueAuthUrl;
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Access validation failed. Please contact administrator.';
      setLocalError(errorMsg);
      console.error('Access validation error:', err);
      setValidatingAccess(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700">
      <div className="bg-white p-8 rounded-lg shadow-2xl w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary-600 mb-2">AliceBlue Trading</h1>
          <p className="text-gray-600">Login to trade your account</p>
        </div>
        <div className="mt-6 p-4 bg-blue-50 rounded-lg text-sm text-gray-700">
          <p className="font-medium mb-2 text-blue-600">📝 Two Ways to Login:</p>
          <div className="p-3 bg-yellow-50 rounded border border-yellow-200">
            <p className="font-semibold text-orange-800 mb-2">⚙️ Method 1: Auto Connect</p>
            <ol className="list-decimal list-inside space-y-1 text-xs">
              <li>
                <a 
                  href={`https://ant.aliceblueonline.com/?appcode=${APP_CODE}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline ml-1"
                >
                  Click To Login Account
                </a>
              </li>
          
            </ol>
          </div>
        
        </div>
        <p className="font-semibold text-orange-700 mb-2">⚙️ Method 2:Login with credentials</p>
          <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              User ID
            </label>
            <input
              type="text"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="input w-full"
              placeholder="Enter your User ID"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Auth Code
            </label>
            <input
              type="text"
              value={authCode}
              onChange={(e) => setAuthCode(e.target.value)}
              className="input w-full"
              placeholder="Enter authorization code"
              required
            />
          </div>

          {(error || localError) && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
              <p className="font-bold">Login Failed</p>
              <p className="text-sm">{localError || error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || validatingAccess}
            className="btn btn-primary w-full"
          >
            {validatingAccess ? 'Validating Access...' : loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        {/* Admin Login Button */}
        <div className="mt-4 text-center">
          <button
            onClick={() => navigate('/admin/login')}
            className="text-sm text-gray-600 hover:text-gray-900 font-medium underline"
          >
            🔑 Admin Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;
